import { Link } from "react-router-dom";
import { MapPin, Clock, Users, Rocket, Wifi, WifiOff, BadgeCheck, Coins } from "lucide-react";
import { useTranslation } from "react-i18next";

const PASTELS = {
  sports: "#E2F3E4",
  social: "#FFE4E1",
  educational: "#EBE4FF",
  entertainment: "#FFF0D4",
};

export default function ActivityCard({ activity, idx = 0 }) {
  const { t } = useTranslation();
  const bg = PASTELS[activity.category] || "#D4F1F4";
  const seatsLeft = activity.seats_left ?? Math.max(0, activity.capacity - (activity.accepted_user_ids?.length || 0));
  const full = seatsLeft === 0;
  const date = activity.start_at ? new Date(activity.start_at) : null;

  const hasCover = !!activity.cover_image;

  return (
    <Link
      to={`/activity/${activity.id}`}
      data-testid={`activity-card-${activity.id}`}
      className="block animate-pop animate-in duration-300"
      style={{ animationDelay: `${idx * 50}ms` }}
    >
      <div 
        className={`card-nb hover:-translate-y-1 hover:shadow-soft-lg transition-all overflow-hidden ${hasCover ? "p-0 bg-white" : "p-5"}`} 
        style={{ background: hasCover ? "white" : bg }}
      >
        {hasCover && (
          <div className="relative h-48 w-full overflow-hidden border-b-2 border-[#0F1115]">
            <img src={activity.cover_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-3 left-3">
              <span className="tag-nb bg-[#FFE08A] text-[#4A3000] border-transparent font-display font-extrabold text-xs shadow-nb-sm">
                {t(`feedPage.cat.${activity.category}`, activity.category)}
              </span>
            </div>
            <div className="absolute top-3 right-3 flex gap-1.5">
              {activity.boosted && (
                <span className="tag-nb bg-gradient-to-r from-[#F9C349] to-[#FFB347] text-[#4A3000] border-transparent shadow-nb-sm">
                  <Rocket className="w-3 h-3" strokeWidth={2.5} />
                </span>
              )}
              {activity.is_online ? (
                <span className="tag-nb bg-white text-[#0F1115] shadow-nb-sm"><Wifi className="w-3 h-3" strokeWidth={2.5} /></span>
              ) : (
                <span className="tag-nb bg-white text-[#0F1115] shadow-nb-sm"><WifiOff className="w-3 h-3" strokeWidth={2.5} /></span>
              )}
            </div>
          </div>
        )}

        <div className={hasCover ? "p-5 space-y-3" : "space-y-3"}>
          {!hasCover && (
            <div className="flex items-start justify-between gap-2 mb-1">
              <span className="tag-nb">
                {t(`feedPage.cat.${activity.category}`, activity.category)}
              </span>
              <div className="flex items-center gap-1.5">
                {activity.boosted && (
                  <span className="tag-nb bg-gradient-to-r from-[#F9C349] to-[#FFB347] text-[#4A3000] border-transparent" data-testid={`boost-badge-${activity.id}`}>
                    <Rocket className="w-3 h-3" strokeWidth={2.5} /> {t("activityDetail.boostedTag")}
                  </span>
                )}
                {activity.is_online ? (
                  <span className="tag-nb"><Wifi className="w-3 h-3" strokeWidth={2.5} /> {t("card.online")}</span>
                ) : (
                  <span className="tag-nb"><WifiOff className="w-3 h-3" strokeWidth={2.5} /> {t("card.irl")}</span>
                )}
              </div>
            </div>
          )}

          <h3 className="font-display font-black text-2xl leading-tight mb-1 break-words tracking-tight text-[#0A0B0D]">{activity.title}</h3>
          
          {/* Host info directly below title as in mockup */}
          <div className="flex items-center gap-2 py-1">
            <div className="w-8 h-8 rounded-full bg-[#FAF8F2] border border-[#0F1115]/10 flex items-center justify-center font-display font-bold overflow-hidden shrink-0 ring-1 ring-black/5">
              {activity.host?.avatar_url ? (
                <img src={activity.host.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="text-xs">{activity.host?.name?.[0]?.toUpperCase() || "?"}</span>
              )}
            </div>
            <span className="text-sm font-bold text-[#0A0B0D] flex items-center gap-1 truncate">
              {activity.host?.name || t("activityDetail.host")}
              {activity.host?.verified && <BadgeCheck className="w-4 h-4 text-[#14C8B0]" strokeWidth={2.5} />}
            </span>
          </div>

          {/* Description (only shown when no cover to keep the card compact) */}
          {activity.description && !hasCover && (
            <p className="text-sm text-[#4A4F58] line-clamp-2">{activity.description}</p>
          )}

          {/* Location and Date tags */}
          <div className="flex flex-wrap gap-2">
            {activity.location && (
              <span className="tag-nb bg-[#FAF8F2] text-[#0A0B0D]"><MapPin className="w-3 h-3" strokeWidth={2.5} /> {activity.location}</span>
            )}
            {date && (
              <span className="tag-nb bg-[#FAF8F2] text-[#0A0B0D]">
                <Clock className="w-3 h-3" strokeWidth={2.5} />
                {date.toLocaleDateString([], { month: "short", day: "numeric" })} · {date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            {activity.price > 0 && (
              <span className="tag-nb bg-gradient-to-r from-[#FFE08A] to-[#F9C349] text-[#4A3000] border-transparent"><Coins className="w-3 h-3" strokeWidth={2.5} /> ${activity.price}</span>
            )}
          </div>

          {/* Spots count row */}
          <div className="flex items-center justify-between pt-3 border-t border-[#0F1115]/8">
            {/* Spots filled (e.g. 1/4) inside a yellow tag-nb */}
            <span className="tag-nb bg-[#FFE08A] text-[#4A3000] border-transparent font-extrabold flex items-center gap-1 shadow-nb-sm">
              <Users className="w-3.5 h-3.5" strokeWidth={2.5} /> {activity.capacity - seatsLeft}/{activity.capacity}
            </span>
            
            {/* Spots left badge */}
            <span className="text-xs font-bold text-[#4A4F58] bg-[#FAF8F2] px-3 py-1.5 rounded-full border border-[#0F1115]/10">
              {full ? t("activityDetail.fullTag") : `${seatsLeft} spots left`}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
